/* eslint-disable no-inline-comments */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';

import { useVerticalNavigationContext } from '../vertical-navigation-context';

export const DEFAULT_POPPER_OPTIONS = {
  placement: 'right-start',
  modifiers: [
    // Modifier allows to hide open popper if trigger is not visible. This
    // happens if popper is open and user scrolls container until trigger
    // is hidden
    { name: 'hide' }
  ]
};

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {boolean} [child]
 * @prop {string} [className]
 * @prop {React.ReactNode} [icon]
 * @prop {string} [label]
 * @prop {() => void} [onClick]
 * @prop {boolean} [selected]
 */
/**
 * @param {Props} props
 */
const MotifVerticalNavigationMenuItem = ({
  child = false,
  children,
  className = '',
  icon = null,
  label = '',
  onClick = () => null,
  selected = false
}) => {
  const { collapse, rootClassName } = useVerticalNavigationContext();
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [hover, setHover] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, DEFAULT_POPPER_OPTIONS);

   useEffect(() => {
    if (collapse) {
      setHover(false);
    }
  }, [collapse]);

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  // Menu is collapsed and this item has a parent Id, render menuItem inside a floating submenu
  if (collapse && child) {
    const subMenuItemClasses = classNames({
      'motif-vertical-navigation-menuitem': true,
      'motif-vertical-navigation-submenuitem': true,
      'motif-vertical-navigation-submenuitem-selected': selected,
      [className]: true
    });

    return (
      <button
        aria-label={label}
        className={subMenuItemClasses}
        data-motif-vertical-navigation-menu-item
        data-testid="motif-vertical-navigation-menuitem"
        onClick={onClick}
        role="menuitem"
      >
        <div className="motif-vertical-navigation-menuitem-label">{children}</div>
      </button>
    );
  }

  const classes = classNames({
    'motif-vertical-navigation-menuitem': true,
    'motif-vertical-navigation-menuitem-child': child,
    'motif-vertical-navigation-menuitem-selected': selected,
    'motif-vertical-navigation-menuitem-hover': hover,
    [className]: true
  });
  const portalClasses = classNames({
    'motif-vertical-navigation-menuitem-portal': true,
    'motif-vertical-navigation-menuitem-selected': selected,
    [className]: true,
    [rootClassName]: true
  });

  return (
    <button
      aria-label={label}
      className={classes}
      data-testid="motif-vertical-navigation-menuitem"
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="menuitem"
      title={label}
    >
      <div className="motif-vertical-navigation-menuitem-iconcontainer" ref={setReferenceElement}>
        {icon}
      </div>
      {/* Portal shows floating label if menu is collapsed */}
      {children && collapse ? hover && ReactDOM.createPortal(
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className={portalClasses}
          data-testid="motif-vertical-navigation-menuitem-portal"
        >
          <div className="motif-vertical-navigation-menuitem-portal-label">{children}</div>
        </div>,
        document.querySelector('body')
      ) : <div className="motif-vertical-navigation-menuitem-label">{children}</div>}
    </button>
  );
};

MotifVerticalNavigationMenuItem.propTypes = {
  child: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)
  ]).isRequired,
  className: PropTypes.string,
  icon: PropTypes.node,
  label: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool
};

export { MotifVerticalNavigationMenuItem };
